/**
 * Base URL for documents stored in INET's S3 gateway.
 *
 * The gateway (s3gw.inet.co.th:8082) asks for a TLS client certificate during
 * the handshake — `ssl_verify_client optional` with an empty CA list. Client
 * auth is optional so the request still succeeds, but any browser holding a
 * client certificate (MDM-enrolled laptops, machines with e-service software)
 * shows a "Select a certificate" picker before it will open the file. That
 * makes every "open document in a new tab" link look broken to those users.
 *
 * Pointing the links at a reverse proxy on our own origin removes that
 * handshake. VUE_APP_INET_S3_BASE selects the origin per environment; it
 * defaults to the direct gateway so any build without a proxy in front of it
 * keeps working exactly as before.
 *
 * Only navigational links (things the user clicks to open in a tab) go through
 * the proxy — inline <img> subresources do not trigger the picker and are left
 * pointing at INET directly.
 */
const INET_S3_ORIGIN = "https://s3gw.inet.co.th:8082";

export const INET_S3_BASE = process.env.VUE_APP_INET_S3_BASE || INET_S3_ORIGIN;

/**
 * Build a URL for an INET S3 object.
 * @param {string} path object path including the bucket, e.g. "/smegp-storage/public/x.pdf"
 * @returns {string}
 */
export function inetS3(path) {
  if (!path) return INET_S3_BASE;
  return `${INET_S3_BASE}${path.startsWith("/") ? path : `/${path}`}`;
}

/** Any INET S3 URL in a body of text. The path is captured for classification. */
const INET_S3_URL = /https:\/\/s3gw\.inet\.co\.th:8082(\/[^\s"'<>\\)]+)/g;

/**
 * Paths worth proxying: things a user clicks to open in a tab. Attachments live
 * under /files/, static documents under /documents/, and SME paperwork in the
 * smegp-documents bucket. Images are excluded on purpose — they load as
 * subresources, never trigger the certificate picker, and routing them through
 * our own nginx would move all that traffic onto the frontend host for nothing.
 */
const DOCUMENT_PATH = /\/files\/|\/documents\/|^\/smegp-documents\//i;

/**
 * Rewrite INET S3 *document* URLs found anywhere in a string onto the proxied
 * base.
 *
 * Not every such URL lives in this repo. Links uploaded through the admin tools
 * were resolved to an absolute INET URL at upload time and stored that way — in
 * news bodies (rendered with v-html), banner and popup records, and SME
 * DOCUMENT_URL fields. Those strings come back from the API long after they
 * were written, so they have to be normalised on the way in rather than fixed
 * at the source.
 *
 * Anything that is not a document URL is left byte-for-byte alone, and the whole
 * function is a no-op when no proxy is configured.
 */
export function toProxiedDocumentUrls(text) {
  if (typeof text !== "string" || INET_S3_BASE === INET_S3_ORIGIN) return text;
  return text.replace(INET_S3_URL, (match, path) =>
    DOCUMENT_PATH.test(path) ? `${INET_S3_BASE}${path}` : match
  );
}
